import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
} from "react";
import { LclDataReducer } from "../Reducers/LclDataReducer";
import { UserDataContext } from "./UserDataContext";
import { roles } from "../Mappings";
import { statuses } from "../Mappings";
import {
  getCurrentBranchesArray,
  getCurrentBranchesObject,
  getDateFromWeekAndDay,
} from "../GlobalFunctions";
import { MenuItem, InputAdornment } from "@material-ui/core";

export const LclDataContext = createContext();

const LclDataContextProvider = (props) => {
  const [lclData, dispatch] = useReducer(LclDataReducer, {});
  const { userData } = useContext(UserDataContext);

  console.log("Rerender");

  const defaultLcl = {
    id: 0,
    reason: "",
    approvalState: 0,
    userAdded: false,
    driverComments: "",
    adminComments: "",
    managerComments: "",
    driver: 0,
    modifiedTime: "1900-01-01T00:00:00",
    branch: "",
    eventType: "",
    eventAddress: "",
    eventAddr1: "",
    loadToDelivery: "1900-01-01T00:00:00",
    carrier: "",
    eventDate: "1900-01-01",
    eventTime: "00:00:00",
    conNoteNum: "",
    consignmentID: 0,
    sender: "",
    receiver: "",
    location: "",
    zone: "",
    pod: "",
    eventItems: "",
    kg: 0,
    m3: 0,
    dpc: 0,
    eu: "",
    eventDateTime: "1900-01-01T00:00:00",
    signature: null,
    consignmentDeliveryID: 0,
    totalItems: 0,
    stopPay: 0,
    totalDPC: 0,
  };

  const comments = {
    [roles.tollAdmin]: "adminComments",
    [roles.ownerDriver]: "driverComments",
    [roles.tollManager]: "managerComments",
  }[userData.roleId];

  const getIsoString = (time, allValues) => {
    const { dateIsoString } = getDateFromWeekAndDay(
      allValues.week,
      allValues.day
    );
    return dateIsoString.replace("00:00:00", `${time}:00`);
  };

  const createLclModel = useCallback(
    (params) => {
      const types = {
        P: "Pickup",
        D: "Delivery",
      };

      const currentBranchArray = getCurrentBranchesArray(
        userData.branches,
        params.week
      );
      const branchShortCodeObject = currentBranchArray.reduce((acc, cur) => {
        acc[cur.shortCode] = cur;
        return acc;
      }, {});

      return {
        branchShortCodeObject, //test only
        eventDateTime: {
          displayName: "Time",
          displayValue: (value) => value,
          validate: (allValues) => true,
          mapToRequestValue: (allValues) =>
            getIsoString(allValues.eventTimeString, allValues),
        },

        eventTimeString: {
          displayName: "Time",
          displayValue: (value) => value,
          validate: (allValues) => allValues.eventTimeString !== "",
          mapToRequestValue: (allValues) => allValues.eventTimeString,
          textFieldProps: {
            name: "eventTimeString",
            type: "time",
            InputLabelProps: {
              shrink: true,
            },
          },
        },

        eventType: {
          displayName: "Type",
          displayValue: (value) => types[value],
          validate: (allValues) => allValues.eventType !== "",
          mapToRequestValue: (allValues) => allValues.eventType,
          textFieldProps: {
            name: "eventType",
            select: true,
            children: Object.keys(types).map((type) => (
              <MenuItem key={type} value={type}>
                {types[type]}
              </MenuItem>
            )),
          },
        },

        branch: {
          displayName: "Branch",
          displayValue: (value) => value,
          // displayValue: (value) => console.log(value),
          validate: (allValues) => allValues.branch !== "",
          mapToRequestValue: (allValues) => allValues.branch,
          textFieldProps: {
            name: "branch",
            select: true,
            children: Object.keys(branchShortCodeObject).map((shortCode) => (
              <MenuItem key={shortCode} value={shortCode}>
                {branchShortCodeObject[shortCode].branchName}
              </MenuItem>
            )),
          },
        },

        eventAddress: {
          displayName: "Address",
          displayValue: (value) => value,
          validate: (allValues) => allValues.eventAddress !== "",
          mapToRequestValue: (allValues) => allValues.eventAddress,
          textFieldProps: {
            name: "eventAddress",
          },
        },

        conNoteNum: {
          displayName: "Con-note Number",
          displayValue: (value) => value,
          validate: (allValues) => allValues.conNoteNum !== "",
          mapToRequestValue: (allValues) => allValues.conNoteNum,
          textFieldProps: {
            name: "conNoteNum",
          },
        },

        sender: {
          displayName: "Sender",
          displayValue: (value) => value,
          validate: (allValues) => {
            if (allValues.type === "P") {
              return allValues.sender !== "";
            } else {
              return true;
            }
          },
          mapToRequestValue: (allValues) => allValues.sender,
          textFieldProps: {
            name: "sender",
          },
        },

        receiver: {
          displayName: "Receiver",
          displayValue: (value) => value,
          validate: (allValues) => {
            if (allValues.type === "D") {
              return allValues.receiver !== "";
            } else {
              return true;
            }
          },
          mapToRequestValue: (allValues) => allValues.receiver,
          textFieldProps: {
            name: "receiver",
          },
        },

        location: {
          displayName: "Location",
          displayValue: (value) => value,
          validate: (allValues) => /^([a-z]|[A-Z])+$/.test(allValues.location),
          mapToRequestValue: (allValues) => allValues.location,
          textFieldProps: {
            name: "location",
          },
        },

        zone: {
          displayName: "Zone",
          displayValue: (value) => value,
          validate: (allValues) => /^([01]?\d)$|^(20)$/.test(allValues.zone),
          mapToRequestValue: (allValues) => allValues.zone,
          textFieldProps: {
            name: "zone",
          },
        },

        eventItems: {
          displayName: "Items",
          displayValue: (value) => value,
          validate: (allValues) => /^\d+$/.test(allValues.eventItems),
          mapToRequestValue: (allValues) => allValues.eventItems,
          textFieldProps: {
            name: "eventItems",
          },
        },

        kg: {
          displayName: "Weight",
          displayValue: (value) => `${Math.round(value)}kg`,
          validate: (allValues) => /^\d+(\.\d{1,3})?$/.test(allValues.kg),
          mapToRequestValue: (allValues) => Number(allValues.kg),
          textFieldProps: {
            name: "kg",
            InputProps: { endAdornment: <InputAdornment>kg</InputAdornment> },
          },
        },

        m3: {
          displayName: "Volume",
          displayValue: (value) =>
            `${(Math.round(value * 1000) / 1000).toFixed(3)}m³`,
          validate: (allValues) => /^\d+(\.\d{1,3})?$/.test(allValues.m3),
          mapToRequestValue: (allValues) => Number(allValues.m3),
          textFieldProps: {
            name: "m3",
            InputProps: {
              endAdornment: (
                <InputAdornment>
                  m
                  <sup>
                    <sup>3</sup>
                  </sup>
                </InputAdornment>
              ),
            },
          },
        },

        dpc: {
          displayName: "DPC",
          displayValue: (value) =>
            `$${(Math.round(value * 100) / 100).toFixed(2)}`,
            validate: (allValues) => /^-?\d+(\.\d{1,12})?$/.test(allValues.dpc),
          mapToRequestValue: (allValues) => Number(allValues.dpc),
          textFieldProps: {
            name: "dpc",
            InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
          },
        },

        stopPay: {
          displayName: "Stop Pay",
          displayValue: (value) =>
            `$${(Math.round(value * 100) / 100).toFixed(2)}`,
            validate: (allValues) => /^-?\d+(\.\d{1,12})?$/.test(allValues.stopPay),
          mapToRequestValue: (allValues) => Number(allValues.stopPay),
          textFieldProps: {
            name: "stopPay",
            InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
          },
        },

        homeDelivery: {
          displayName: "Home Delivery",
          displayValue: (value) =>
            `$${(Math.round(value * 100) / 100).toFixed(2)}`,
          validate: (allValues) =>
            /^-?\d+(\.\d{1,12})?$/.test(allValues.homeDelivery),
          mapToRequestValue: (allValues) => Number(allValues.homeDelivery),
          textFieldProps: {
            name: "homeDelivery",
            InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
          },
        },

        totalDPC: {
          displayName: "Total DPC",
          displayValue: (value) =>
            `$${(Math.round(value * 100) / 100).toFixed(2)}`,
          validate: (allValues) => true,
          calculateValue: (allValues) =>
            Number(allValues.dpc) + Number(allValues.stopPay),
          mapToRequestValue: (allValues) => Number(allValues.totalDPC),
          textFieldProps: {
            name: "totalDPC",
            InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
          },
        },

        reason: {
          displayName: "Reason",
          displayValue: (value) => value,
          validate: (allValues) => allValues.reason !== "",
          mapToRequestValue: (allValues) => allValues.reason,
          textFieldProps: {
            name: "reason",
            placeholder: "Choose one",
            select: true,
          },
        },

        driverComments: {
          displayName: "Driver Comments",
          displayValue: (value) => value,
          validate: (allValues) => true,
          mapToRequestValue: (allValues) => allValues.driverComments,
          getValueFromRequest: (value) => value,
          textFieldProps: {
            name: "driverComments",
            placeholder: "Provide a brief description",
            multiline: true,
            variant: "outlined",
            rows: 3,
            rowsMax: 3,
          },
        },

        adminComments: {
          displayName: "Admin Comments",
          displayValue: (value) => value,
          validate: (allValues) => true,
          mapToRequestValue: (allValues) => allValues.adminComments,
          getValueFromRequest: (value) => value,
          textFieldProps: {
            name: "adminComments",
            placeholder: "Provide a brief description",
            multiline: true,
            variant: "outlined",
            rows: 3,
            rowsMax: 3,
          },
        },

        managerComments: {
          displayName: "Manager Comments",
          displayValue: (value) => value,
          validate: (allValues) => true,
          mapToRequestValue: (allValues) => allValues.managerComments,
          getValueFromRequest: (value) => value,
          textFieldProps: {
            name: "managerComments",
            placeholder: "Provide a brief description",
            multiline: true,
            variant: "outlined",
            rows: 3,
            rowsMax: 3,
          },
        },

        approvalState: {
          displayName: "Approval State",
          displayValue: (value, allValues) => statuses[value],
          validate: (allValues) => true,
          mapToRequestValue: (allValues) => allValues.approvalState,
          getValueFromRequest: (value) => value,
        },
      };
    },
    [userData.branches]
  );

  // const displayColumns = [
  //   "eventTime",
  //   "eventType",
  //   "branch",
  //   "eventAddress",
  //   "conNoteNum",
  //   "sender",
  //   "receiver",
  //   "location",
  //   "zone",
  //   "eventItems",
  //   "kg",
  //   "m3",
  //   "dpc",
  //   "stopPay",
  //   "homeDelivery",
  //   "reason",
  // ];

  return (
    <LclDataContext.Provider
      value={{
        lclData,
        dispatch,
        createLclModel,
        comments,
        defaultLcl,
        // branchesObject,
      }}
    >
      {props.children}
    </LclDataContext.Provider>
  );
};

export default LclDataContextProvider;
